import { z } from 'zod'

const EMAIL_MAX_LENGTH = 255
const PASSWORD_MIN_LENGTH = 12
const PASSWORD_MAX_LENGTH = 512

const usernameSchema = z
  .string({
    error: 'Vul je gebruikersnaam in.',
  })
  .trim()
  .min(1, 'Vul je gebruikersnaam in.')
  .max(255, `De gebruikersnaam mag maximaal 255 tekens bevatten.`)

const passwordSchema = z
  .string()
  .min(1, 'Vul je wachtwoord in.')
  .max(PASSWORD_MAX_LENGTH, `Het wachtwoord mag maximaal ${EMAIL_MAX_LENGTH} tekens bevatten.`)

const securePasswordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Het wachtwoord moet minimaal ${PASSWORD_MIN_LENGTH} tekens bevatten.`)
  .max(PASSWORD_MAX_LENGTH, `Het wachtwoord mag maximaal ${EMAIL_MAX_LENGTH} tekens bevatten.`)
  .regex(/[a-z]/, 'Gebruik minimaal één kleine letter.')
  .regex(/[A-Z]/, 'Gebruik minimaal één hoofdletter.')
  .regex(/[0-9]/, 'Gebruik minimaal één cijfer.')
  .regex(/[^a-zA-Z0-9]/, 'Gebruik minimaal één speciaal teken.')

export const userLoginRequestSchema = z
  .object({
    username: usernameSchema,
    password: passwordSchema,
  })
  .strict()
  .brand<'UserLoginRequest'>()
export type UserLoginRequest = z.infer<typeof userLoginRequestSchema>

const optionalNameSchema = z.string().trim().min(1).max(255).nullish()

export const userSchema = z.object({
  username: usernameSchema,
  firstName: optionalNameSchema,
  lastName: optionalNameSchema,
  roles: z
    .array(z.string().min(1))
    .nullish()
    .transform((roles) => roles ?? []),
})
export type User = z.infer<typeof userSchema>
userSchema.transform((data): User => {
  const result: User = {
    username: data.username,
    roles: data.roles,
  }

  if (data.firstName != null) {
    result.firstName = data.firstName
  }

  if (data.lastName != null) {
    result.lastName = data.lastName
  }

  return result
})

export const changePasswordRequestSchema = z
  .object({
    username: usernameSchema,
    password: securePasswordSchema,
  })
  .strict()
  .brand<'ChangePasswordRequest'>()
export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>

export const forgotPasswordRequestSchema = z
  .object({
    username: usernameSchema,
    password: securePasswordSchema,
  })
  .strict()
  .brand<'ForgotPasswordRequest'>()
export type ForgotPasswordRequest = z.infer<typeof forgotPasswordRequestSchema>
